import type { ApiResponse } from "api/ApiResponses";
import ProcessDesignApi, {
  type CreateProcessDesignRequest,
  type ExecutionsStatus,
  type ProcessDesignDetailsRequest,
  type ProcessDesignRequest,
} from "api/ProcessDesignApi";
import type {
  ReduxAction,
  ReduxActionWithCallbacks,
} from "ee/constants/ReduxActionConstants";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import { call, put } from "redux-saga/effects";
import { validateResponse } from "sagas/ErrorSagas";

/**
 * 获取流程设计列表
 * @param action
 */
export function* getProcessDesignSaga(
  action: ReduxAction<ProcessDesignRequest>,
) {
  try {
    const cleanedPayload = Object.fromEntries(
      Object.entries(action.payload).filter(([_, value]) => value != null),
    );
    const response: ApiResponse = yield call(
      ProcessDesignApi.getProcessDesign,
      cleanedPayload,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_FETCH_PROCESS_DESIGN_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 获取流程设计详情
 * @param action
 */
export function* getProcessDesignDetailsSaga(
  action: ReduxAction<ProcessDesignDetailsRequest>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.getProcessDesignDetails,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_FETCH_PROCESS_DESIGN_DETAILS_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 创建流程设计
 * @param action
 */
export function* createProcessDesignSaga(
  action: ReduxAction<CreateProcessDesignRequest>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.createProcessDesign,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_CREATE_PROCESS_DESIGN_SUCCESS,
        payload: {},
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 更新流程设计
 * @param action
 */
export function* updateProcessDesignSaga(
  action: ReduxActionWithCallbacks<CreateProcessDesignRequest, any, any>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.updateProcessDesign,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_UPDATE_PROCESS_DESIGN_SUCCESS,
        payload: {},
      });

      // 调用成功回调
      if (action.onSuccessCallback) {
        action.onSuccessCallback(response.data);
      }
    }
  } catch (error) {
    console.log(error);

    // 调用错误回调
    if (action.onErrorCallback) {
      action.onErrorCallback(error);
    }
  }
}

/**
 * 删除流程设计
 * @param action
 */
export function* deleteProcessDesignSaga(action: ReduxAction<{ id: string }>) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.deleteProcessDesign,
      action.payload.id,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_DELETE_PROCESS_DESIGN_SUCCESS,
        payload: {},
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 查询流程申请列表
 */
export function* getProcessApplicationListSaga(
  action: ReduxActionWithCallbacks<
    {
      status?: ExecutionsStatus;
    },
    any,
    any
  >,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.getProcessApplicationList,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_FETCH_PROCESS_APPLICATION_LIST_SUCCESS,
        payload: response.data,
      });
      // 调用成功回调
      if (action.onSuccessCallback) {
        action.onSuccessCallback(response.data);
      }
    }
  } catch (error) {
    console.log(error);

    // 调用错误回调
    if (action.onErrorCallback) {
      action.onErrorCallback(error);
    }
  }
}

/**
 * 查询流程申请详情
 */
export function* getProcessApplicationDetailsSaga(
  action: ReduxAction<{
    executionId: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.getProcessApplicationDetails,
      action.payload.executionId,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_FETCH_PROCESS_APPLICATION_DETAILS_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 启动流程申请
 */
export function* startApprovalProcessSaga(
  action: ReduxAction<{
    processId: string;
    title: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.startProcessApplication,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_START_PROCESS_APPLICATION_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 取消流程申请
 */
export function* cancelProcessApplicationSaga(
  action: ReduxAction<{
    executionId: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.cancelProcessApplication,
      action.payload.executionId,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_CANCEL_PROCESS_APPLICATION_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 查询任务列表
 */
export function* getTaskListSaga(
  action: ReduxAction<{
    scope?: "me" | "all";
    useDone?: boolean;
    canHandle?: boolean;
    executionId?: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.getTaskList,
      action.payload,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_FETCH_TASK_LIST_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 查询任务详情
 */
export function* getTaskDetailsSaga(
  action: ReduxAction<{
    taskId: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.getTaskDetails,
      action.payload.taskId,
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_FETCH_TASK_DETAILS_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 审批通过
 */
export function* approveTaskSaga(
  action: ReduxAction<{
    taskId: string;
    comment: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.approveTask,
      action.payload.taskId,
      {
        comment: action.payload.comment,
      },
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_APPROVE_TASK_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 审批不通过
 */
export function* rejectTaskSaga(
  action: ReduxAction<{
    taskId: string;
    comment: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.rejectTask,
      action.payload.taskId,
      {
        comment: action.payload.comment,
      },
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_REJECT_TASK_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 转交任务
 */
export function* transferTaskSaga(
  action: ReduxAction<{
    taskId: string;
    assigneeUserId: string;
    comment: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.transferTask,
      action.payload.taskId,
      {
        assigneeUserId: action.payload.assigneeUserId,
        comment: action.payload.comment,
      },
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_TRANSFER_TASK_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 发表评论
 */
export function* commentTaskSaga(
  action: ReduxAction<{
    taskId: string;
    comment: string;
  }>,
) {
  try {
    const response: ApiResponse = yield call(
      ProcessDesignApi.commentTask,
      action.payload.taskId,
      {
        comment: action.payload.comment,
      },
    );
    const isValidResponse: boolean = yield validateResponse(response);
    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.AF_COMMENT_TASK_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}
